import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  Row,
  Col,
  Radio,
  Divider,
  Upload,
  Image,
  Checkbox,
  DatePicker,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InboxOutlined, DeleteOutlined as DeleteIcon } from '@ant-design/icons';
import api, { getImageUrl } from '../services/api';
import dayjs from 'dayjs';
import PlanoCarro from '../components/PlanoCarro';
import { useAuth } from '../contexts/AuthContext';

const Servicios = () => {
  const { user } = useAuth();
  const [servicios, setServicios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [tiposServicio, setTiposServicio] = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [personalDisponible, setPersonalDisponible] = useState([]);
  const [asistencias, setAsistencias] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  
  // Verificar si es admin principal (sin empresa_id)
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [clienteModalVisible, setClienteModalVisible] = useState(false);
  const [vehiculoModalVisible, setVehiculoModalVisible] = useState(false);
  const [editingServicio, setEditingServicio] = useState(null);
  const [form] = Form.useForm();
  const [clienteForm] = Form.useForm();
  const [vehiculoForm] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [comprobanteAdelanto, setComprobanteAdelanto] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [esExtemporaneo, setEsExtemporaneo] = useState(false);
  const [modoSoloLectura, setModoSoloLectura] = useState(false);

  useEffect(() => {
    fetchServicios();
    fetchClientes();
    fetchVehiculos();
    fetchTiposServicio();
    fetchAseguradoras();
    if (isMainAdmin) {
      fetchEmpresas();
    }
    fetchPersonal();
    fetchAsistencias();
    fetchMetodosPago();
  }, [isMainAdmin]);

  // Filtrar personal disponible (con asistencia activa y sin servicio en curso)
  useEffect(() => {
    const personalConAsistencia = personal.filter(p => {
      // Verificar si tiene asistencia activa (tipo 'trabajo', con hora_ingreso pero sin hora_salida)
      const asistencia = asistencias.find(a => 
        a.personal_id === p.id && 
        a.tipo === 'trabajo' &&
        a.hora_ingreso &&
        !a.hora_salida
      );
      
      if (!asistencia) {
        return false; // No tiene asistencia activa
      }
      
      // Verificar que NO esté en un servicio actualmente (estado no es 'completado' ni 'cancelado')
      const servicioActivo = servicios.find(s => 
        s.trabajador_id === p.id && 
        s.estado !== 'completado' && 
        s.estado !== 'cancelado'
      );
      
      return !servicioActivo; // Solo disponible si no tiene servicio activo
    });
    setPersonalDisponible(personalConAsistencia);
  }, [personal, asistencias, servicios]);

  const fetchServicios = async () => {
    setLoading(true);
    try {
      const response = await api.get('/servicios');
      // Asegurar que siempre sea un array
      setServicios(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar servicios:', error);
      message.error('Error al cargar servicios');
      setServicios([]); // Asegurar que sea un array incluso en caso de error
    } finally {
      setLoading(false);
    }
  };

  const fetchClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const fetchVehiculos = async () => {
    try {
      const response = await api.get('/vehiculos');
      setVehiculos(response.data);
    } catch (error) {
      console.error('Error al cargar vehículos');
    }
  };

  const fetchTiposServicio = async () => {
    try {
      const response = await api.get('/tipos-servicio');
      setTiposServicio(response.data.filter(t => t.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar tipos de servicio');
    }
  };

  const fetchAseguradoras = async () => {
    try {
      const response = await api.get('/aseguradoras');
      setAseguradoras(response.data.filter(a => a.estado === 'activa'));
    } catch (error) {
      console.error('Error al cargar aseguradoras');
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error al cargar empresas');
    }
  };

  const fetchPersonal = async () => {
    try {
      const response = await api.get('/personal');
      setPersonal(response.data.filter(p => p.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar personal');
    }
  };

  const fetchAsistencias = async () => {
    try {
      const response = await api.get('/asistencias');
      setAsistencias(response.data);
    } catch (error) {
      console.error('Error al cargar asistencias');
    }
  };

  const fetchMetodosPago = async () => {
    try {
      const response = await api.get('/metodos-pago');
      setMetodosPago(response.data.filter(m => m.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar métodos de pago');
    }
  };

  const buscarTarifaBase = async (aseguradoraId, tipoServicioId, forzarActualizacion = false) => {
    if (!aseguradoraId || !tipoServicioId) {
      return;
    }
    try {
      const response = await api.get(`/tarifas-base?aseguradora_id=${aseguradoraId}&tipo_servicio_id=${tipoServicioId}`);
      if (response.data && response.data.precio) {
        // Si se fuerza la actualización o no hay costo actual, actualizar el costo
        const costoActual = form.getFieldValue('costo');
        if (forzarActualizacion || !costoActual || costoActual === 0) {
          form.setFieldsValue({ costo: parseFloat(response.data.precio) });
        }
      } else {
        // Si no se encuentra tarifa base, limpiar el costo si se está forzando la actualización
        if (forzarActualizacion) {
          form.setFieldsValue({ costo: undefined });
        }
      }
    } catch (error) {
      // Si no se encuentra tarifa base, limpiar el costo si se está forzando la actualización
      if (forzarActualizacion) {
        form.setFieldsValue({ costo: undefined });
      }
      console.log('No se encontró tarifa base para esta combinación');
    }
  };

  const handleAsignarPersonal = async (servicioId, trabajadorId) => {
    try {
      await api.put(`/servicios/${servicioId}`, { trabajador_id: trabajadorId || null });
      message.success('Personal asignado exitosamente');
      fetchServicios();
    } catch (error) {
      message.error('Error al asignar personal');
    }
  };

  const handleCrearCliente = async (values) => {
    try {
      const response = await api.post('/clientes', values);
      message.success('Cliente creado exitosamente');
      setClienteModalVisible(false);
      clienteForm.resetFields();
      await fetchClientes();
      const clienteId = response.data.id || response.data;
      form.setFieldsValue({ cliente_id: clienteId.toString() });
    } catch (error) {
      message.error('Error al crear cliente');
    }
  };

  const handleCrearVehiculo = async (values) => {
    try {
      const clienteId = form.getFieldValue('cliente_id');
      if (!clienteId) {
        message.warning('Primero selecciona un cliente');
        return;
      }
      const data = { ...values, cliente_id: clienteId };
      const response = await api.post('/vehiculos', data);
      message.success('Vehículo creado exitosamente');
      setVehiculoModalVisible(false);
      vehiculoForm.resetFields();
      await fetchVehiculos();
      const nuevoVehiculo = response.data;
      // El backend devuelve el objeto completo del vehículo
      const vehiculoData = nuevoVehiculo;
      form.setFieldsValue({ 
        vehiculo_id: vehiculoData.id,
        placa: vehiculoData.placa || values.placa,
        marca_vehiculo: vehiculoData.marca || values.marca,
        modelo_vehiculo: vehiculoData.modelo || values.modelo,
        anio_vehiculo: vehiculoData.anio || vehiculoData.año || values.anio || values.año,
        tipo_vehiculo: `${vehiculoData.marca || values.marca} ${vehiculoData.modelo || values.modelo}`
      });
      // Agregar el nuevo vehículo a la lista
      if (vehiculoData.placa) {
        setVehiculos([...vehiculos, vehiculoData]);
      }
    } catch (error) {
      message.error('Error al crear vehículo');
    }
  };

  const handlePlacaChange = (placa) => {
    const vehiculo = vehiculos.find(v => v.placa === placa);
    if (vehiculo) {
      form.setFieldsValue({
        vehiculo_id: vehiculo.id,
        marca_vehiculo: vehiculo.marca,
        modelo_vehiculo: vehiculo.modelo,
        anio_vehiculo: vehiculo.anio || vehiculo.año,
        tipo_vehiculo: `${vehiculo.marca} ${vehiculo.modelo}`
      });
    } else {
      form.setFieldsValue({
        vehiculo_id: null,
        marca_vehiculo: null,
        modelo_vehiculo: null,
        anio_vehiculo: null,
        tipo_vehiculo: null
      });
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // Separar campos del servicio de los campos del checklist
      const checklistFields = [
        'tarjeta_propiedad', 'soat', 'cabeceras', 'llave_control', 'encendedor',
        'radio_mascarilla', 'parlantes', 'ceniceros', 'tapa_sol', 'tapa_radiador',
        'varilla_aceite', 'pisos', 'faros_delanteros', 'faros_posteriores', 'plumillas',
        'tapa_combustible', 'antena', 'espejo_interior', 'espejo_exterior', 'bateria',
        'tapa_aceite', 'extintor', 'triangulo_cono', 'seguro_rueda', 'gata',
        'llave_rueda', 'vasos_copas', 'aros', 'llanta_repuesto', 'tapa_liq_emb', 'emblema'
      ];
      
      const servicioData = {};
      const checklistData = {};
      
      // Separar los campos
      Object.keys(values).forEach(key => {
        if (checklistFields.includes(key)) {
          checklistData[key] = values[key];
        } else {
          servicioData[key] = values[key];
        }
      });

      // Subir imágenes y comprobante en paralelo si es posible
      let imagenesUrls = [];
      let comprobanteAdelantoUrl = null;
      
      // Preparar datos para subidas
      const formDataImagenes = new FormData();
      const existingUrls = [];
      
      if (fileList.length > 0) {
        fileList.forEach((file) => {
          if (file.originFileObj) {
            formDataImagenes.append('imagenes[]', file.originFileObj);
          } else if (file.url) {
            existingUrls.push(file.url);
          }
        });
      }
      
      const formDataComprobante = new FormData();
      let tieneComprobanteNuevo = false;
      if (comprobanteAdelanto.length > 0) {
        const comprobanteFile = comprobanteAdelanto[0];
        if (comprobanteFile.originFileObj) {
          formDataComprobante.append('imagenes[]', comprobanteFile.originFileObj);
          tieneComprobanteNuevo = true;
        } else if (comprobanteFile.url) {
          comprobanteAdelantoUrl = comprobanteFile.url;
        }
      }
      
      // Realizar subidas en paralelo
      const uploadPromises = [];
      
      if (formDataImagenes.has('imagenes[]')) {
        uploadPromises.push(
          api.post('/servicios/upload-images', formDataImagenes, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }).then(response => {
            imagenesUrls = [...existingUrls, ...response.data.urls];
          }).catch(error => {
            console.error('Error al subir imágenes:', error);
            throw new Error('Error al subir imágenes');
          })
        );
      } else {
        imagenesUrls = existingUrls;
      }
      
      if (tieneComprobanteNuevo) {
        uploadPromises.push(
          api.post('/servicios/upload-images', formDataComprobante, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }).then(response => {
            if (response.data.urls && response.data.urls.length > 0) {
              comprobanteAdelantoUrl = response.data.urls[0];
            }
          }).catch(error => {
            console.error('Error al subir comprobante:', error);
            throw new Error('Error al subir comprobante de adelanto');
          })
        );
      }
      
      // Esperar a que todas las subidas terminen
      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }
      
      // Formatear fecha_solicitud si es extemporáneo
      if (esExtemporaneo && values.fecha_solicitud) {
        servicioData.fecha_solicitud = dayjs(values.fecha_solicitud).format('YYYY-MM-DD HH:mm:ss');
      }
      
      const data = {
        ...servicioData,
        checklist: checklistData,
        imagenes: imagenesUrls,
        fallas_mecanicas: values.fallas_mecanicas || [],
        comprobante_adelanto: comprobanteAdelantoUrl
      };
      
      if (editingServicio) {
        await api.put(`/servicios/${editingServicio.id}`, data);
      } else {
        await api.post('/servicios', data);
      }
      
      setModalVisible(false);
      form.resetFields();
      setEditingServicio(null);
      setFileList([]);
      setComprobanteAdelanto([]);
      setEsExtemporaneo(false);
      
      // Recargar servicios en segundo plano sin bloquear la UI
      fetchServicios().catch(err => {
        console.error('Error al recargar servicios:', err);
      });
      
      message.success(editingServicio ? 'Servicio actualizado exitosamente' : 'Servicio creado exitosamente');
    } catch (error) {
      console.error('Error al guardar servicio:', error);
      const errorMessage = error.message || 'Error al guardar servicio';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (record) => {
    // Actualizar asistencias antes de abrir el modal
    await fetchAsistencias();
    setEditingServicio(record);
    setEsExtemporaneo(false); // Resetear checkbox al editar
    setModoSoloLectura(record.estado === 'completado'); // Activar modo solo lectura si está completado
    const formValues = {
      ...record,
      cliente_id: record.cliente_id?.toString(),
      vehiculo_id: record.vehiculo_id?.toString(),
      tipo_servicio_id: record.tipo_servicio_id?.toString(),
      aseguradora_id: record.aseguradora_id?.toString(),
      empresa_id: record.empresa_id ? record.empresa_id.toString() : undefined,
      // Convertir año_vehiculo a anio_vehiculo si existe
      anio_vehiculo: record.anio_vehiculo || record.año_vehiculo,
    };
    
    // Eliminar año_vehiculo si existe para evitar conflictos
    if (formValues.año_vehiculo !== undefined) {
      delete formValues.año_vehiculo;
    }
    
    if (record.checklist) {
      Object.keys(record.checklist).forEach(key => {
        if (key !== 'id' && key !== 'servicio_id') {
          formValues[key] = record.checklist[key];
        }
      });
    }
    
    // Cargar imágenes existentes
    if (record.imagenes && Array.isArray(record.imagenes) && record.imagenes.length > 0) {
      const existingFiles = record.imagenes.map((url, index) => ({
        uid: `existing-${index}`,
        name: `imagen-${index + 1}.jpg`,
        status: 'done',
        url: getImageUrl(url),
        thumbUrl: getImageUrl(url),
      }));
      setFileList(existingFiles);
    } else {
      setFileList([]);
    }

    // Cargar comprobante de adelanto si existe
    if (record.comprobante_adelanto) {
      setComprobanteAdelanto([{
        uid: 'comprobante-adelanto',
        name: 'comprobante-adelanto.jpg',
        status: 'done',
        url: record.comprobante_adelanto,
        thumbUrl: record.comprobante_adelanto,
      }]);
    } else {
      setComprobanteAdelanto([]);
    }
    
    // Cargar fallas mecánicas si existen
    if (record.fallas_mecanicas && Array.isArray(record.fallas_mecanicas)) {
      formValues.fallas_mecanicas = record.fallas_mecanicas;
    } else {
      formValues.fallas_mecanicas = [];
    }
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/servicios/${id}`);
      message.success('Servicio eliminado exitosamente');
      fetchServicios();
    } catch (error) {
      message.error('Error al eliminar servicio');
    }
  };

  const checklistItems = [
    { key: 'tarjeta_propiedad', label: 'Tarjeta de Propiedad' },
    { key: 'soat', label: 'SOAT' },
    { key: 'cabeceras', label: 'Cabeceras' },
    { key: 'llave_control', label: 'Llave - Control/R' },
    { key: 'encendedor', label: 'Encendedor' },
    { key: 'radio_mascarilla', label: 'Radio - Mascarilla' },
    { key: 'parlantes', label: 'Parlantes' },
    { key: 'ceniceros', label: 'Ceniceros' },
    { key: 'tapa_sol', label: 'Tapa sol' },
    { key: 'tapa_radiador', label: 'Tapa de Radiador' },
    { key: 'varilla_aceite', label: 'Varilla de Aceite' },
    { key: 'pisos', label: 'Pisos' },
    { key: 'faros_delanteros', label: 'Faros Delanteros' },
    { key: 'faros_posteriores', label: 'Faros Posteriores' },
    { key: 'plumillas', label: 'Plumillas' },
    { key: 'tapa_combustible', label: 'Tapa Combustible' },
    { key: 'antena', label: 'Antena' },
    { key: 'espejo_interior', label: 'Espejo Interior' },
    { key: 'espejo_exterior', label: 'Espejo Exterior' },
    { key: 'bateria', label: 'Bateria' },
    { key: 'tapa_aceite', label: 'Tapa de Aceite' },
    { key: 'extintor', label: 'Extintor' },
    { key: 'triangulo_cono', label: 'Triangulo / Cono' },
    { key: 'seguro_rueda', label: 'Seguro de Rueda' },
    { key: 'gata', label: 'Gata' },
    { key: 'llave_rueda', label: 'Llave / Rueda' },
    { key: 'vasos_copas', label: 'Vasos / Copas' },
    { key: 'aros', label: 'Aros' },
    { key: 'llanta_repuesto', label: 'Llanta / Repuesto' },
    { key: 'tapa_liq_emb', label: 'Tapa de Liq. de Emb' },
    { key: 'emblema', label: 'Emblema' },
  ];

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, record) =>
        `${record.cliente_nombre || ''} ${record.cliente_apellido || ''}`,
    },
    {
      title: 'Placa',
      dataIndex: 'placa',
      key: 'placa',
    },
    {
      title: 'Tipo de Servicio',
      dataIndex: 'tipo_servicio_nombre',
      key: 'tipo_servicio',
    },
    {
      title: 'Aseguradora',
      dataIndex: 'aseguradora_nombre',
      key: 'aseguradora',
    },
    ...(isMainAdmin ? [{
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      key: 'empresa',
      render: (empresa) => empresa || <Tag color="default">Sin empresa</Tag>,
    }] : []),
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          pendiente: 'orange',
          en_espera: 'cyan',
          en_proceso: 'blue',
          completado: 'green',
          cancelado: 'red',
        };
        const labels = {
          pendiente: 'PENDIENTE',
          en_espera: 'EN ESPERA',
          en_proceso: 'EN PROCESO',
          completado: 'COMPLETADO',
          cancelado: 'CANCELADO',
        };
        return <Tag color={colors[estado] || 'default'}>{labels[estado] || estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Personal Asignado',
      key: 'trabajador',
      width: 200,
      render: (_, record) => (
        record.trabajador_nombre ? (
          <span>{record.trabajador_nombre}</span>
        ) : (
          <span style={{ color: '#999' }}>Sin asignar</span>
        )
      ),
    },
    {
      title: 'Fecha Solicitud',
      dataIndex: 'fecha_solicitud',
      key: 'fecha_solicitud',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 150,
      render: (_, record) => {
        const esCompletado = record.estado === 'completado';
        return (
          <Space size="small" wrap>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              disabled={esCompletado}
            >
              <span className="hide-on-mobile">{esCompletado ? 'Ver' : 'Editar'}</span>
            </Button>
            {!esCompletado && (
              <Popconfirm
                title="¿Estás seguro de eliminar este servicio?"
                onConfirm={() => handleDelete(record.id)}
                okText="Sí"
                cancelText="No"
              >
                <Button type="primary" danger icon={<DeleteOutlined />} size="small">
                  <span className="hide-on-mobile">Eliminar</span>
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Servicios"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={async () => {
              // Actualizar asistencias antes de abrir el modal
              await fetchAsistencias();
              setEditingServicio(null);
              form.resetFields();
              setFileList([]);
              setComprobanteAdelanto([]);
              setEsExtemporaneo(false);
              setModoSoloLectura(false);
              setModalVisible(true);
            }}
          >
            <span className="hide-on-mobile">Nuevo Servicio</span>
            <span className="show-on-mobile">Nuevo</span>
          </Button>
        }
      >
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={servicios}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </Card>

      <Modal
        title={editingServicio ? (modoSoloLectura ? 'Ver Servicio' : 'Editar Servicio') : 'Nuevo Servicio'}
        open={modalVisible}
        width="95%"
        style={{ maxWidth: 1200 }}
        bodyStyle={{ maxHeight: '90vh', overflowY: 'auto' }}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingServicio(null);
          setFileList([]);
          setComprobanteAdelanto([]);
          setEsExtemporaneo(false);
          setModoSoloLectura(false);
        }}
        footer={null}
        width="95%"
        style={{ maxWidth: 1200, top: 20 }}
        bodyStyle={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        <Form form={form} layout="vertical" onFinish={modoSoloLectura ? undefined : handleSubmit}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="extemporaneo" valuePropName="checked">
                <Checkbox 
                  disabled={modoSoloLectura}
                  onChange={(e) => {
                    setEsExtemporaneo(e.target.checked);
                    if (!e.target.checked) {
                      // Si se desmarca, limpiar fecha y trabajador
                      form.setFieldsValue({ 
                        fecha_solicitud: undefined,
                        trabajador_id: undefined 
                      });
                    }
                  }}
                >
                  Extemporáneo
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          {esExtemporaneo && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  name="fecha_solicitud" 
                  label="Fecha del Servicio"
                  rules={[{ required: true, message: 'Selecciona la fecha del servicio' }]}
                >
                  <DatePicker 
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="Selecciona la fecha"
                    disabled={modoSoloLectura}
                    disabledDate={(current) => {
                      // Permitir cualquier fecha pasada o presente, no futuras
                      return current && current > dayjs().endOf('day');
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cliente_id"
                label="Usuario/Cliente"
                rules={[{ required: true, message: 'Selecciona un cliente' }]}
              >
                <Select
                  disabled={modoSoloLectura}
                  placeholder="Selecciona un cliente"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={() => setClienteModalVisible(true)}
                        style={{ width: '100%' }}
                      >
                        Crear nuevo cliente
                      </Button>
                    </>
                  )}
                >
                  {clientes.map((cliente) => (
                    <Select.Option key={cliente.id} value={cliente.id}>
                      {cliente.nombre} {cliente.apellido}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="placa"
                label="Placa"
                rules={[{ required: true, message: 'Ingresa la placa' }]}
              >
                <Select
                  placeholder="Selecciona o ingresa una placa"
                  showSearch
                  allowClear
                  onChange={handlePlacaChange}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={() => setVehiculoModalVisible(true)}
                        style={{ width: '100%' }}
                      >
                        Registrar nuevo vehículo
                      </Button>
                    </>
                  )}
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {vehiculos.map((vehiculo) => (
                    <Select.Option key={vehiculo.id} value={vehiculo.placa}>
                      {vehiculo.placa} - {vehiculo.marca} {vehiculo.modelo}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="lugar_contacto" label="Lugar de Contacto">
                <Input disabled={modoSoloLectura} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lugar_destino" label="Lugar de Destino">
                <Input disabled={modoSoloLectura} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="marca_vehiculo" label="Marca del Vehículo">
                <Input disabled={modoSoloLectura} placeholder="Se completa automáticamente" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="modelo_vehiculo" label="Modelo del Vehículo">
                <Input disabled={modoSoloLectura} placeholder="Se completa automáticamente" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="tipo_vehiculo" label="Tipo de Vehículo">
                <Input disabled={modoSoloLectura} placeholder="Se completa automáticamente" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="anio_vehiculo" label="Año del Vehículo">
                <InputNumber disabled={modoSoloLectura} min={1900} max={new Date().getFullYear() + 1} style={{ width: '100%' }} placeholder="Se completa automáticamente" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="tipo_servicio_id"
                label="Tipo de Servicio"
                rules={[{ required: true, message: 'Selecciona un tipo de servicio' }]}
              >
                <Select 
                  disabled={modoSoloLectura}
                  placeholder="Selecciona el tipo de servicio"
                  onChange={(value) => {
                    const aseguradoraId = form.getFieldValue('aseguradora_id');
                    if (aseguradoraId && value) {
                      // Forzar actualización de tarifa cuando cambia el tipo de servicio
                      buscarTarifaBase(aseguradoraId, value, true);
                    } else if (!value) {
                      // Si se limpia el tipo de servicio, limpiar el costo
                      form.setFieldsValue({ costo: undefined });
                    }
                  }}
                >
                  {tiposServicio.map((tipo) => (
                    <Select.Option key={tipo.id} value={tipo.id}>
                      {tipo.codigo} - {tipo.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="aseguradora_id" label="Ordenante/Aseguradora">
                <Select 
                  disabled={modoSoloLectura}
                  placeholder="Selecciona una aseguradora" 
                  allowClear
                  onChange={(value) => {
                    const tipoServicioId = form.getFieldValue('tipo_servicio_id');
                    if (tipoServicioId && value) {
                      // Forzar actualización de tarifa cuando cambia la aseguradora
                      buscarTarifaBase(value, tipoServicioId, true);
                    } else if (!value) {
                      // Si se limpia la aseguradora, limpiar el costo
                      form.setFieldsValue({ costo: undefined });
                    }
                  }}
                >
                  {aseguradoras.map((aseguradora) => (
                    <Select.Option key={aseguradora.id} value={aseguradora.id}>
                      {aseguradora.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {isMainAdmin && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="empresa_id"
                  label="Empresa"
                  tooltip="Asignar este servicio a una empresa específica"
                >
                  <Select
                    disabled={modoSoloLectura}
                    placeholder="Selecciona la empresa (opcional)"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {empresas.map((e) => (
                      <Select.Option key={e.id} value={e.id}>
                        {e.nombre}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="trabajador_id" 
                label="Asignar Personal"
                tooltip={esExtemporaneo ? "Al marcar extemporáneo, puedes seleccionar cualquier personal activo" : "Solo se muestran operarios que han marcado su asistencia y están trabajando"}
              >
                <Select
                  disabled={modoSoloLectura}
                  placeholder={esExtemporaneo ? "Selecciona el personal (cualquier personal activo)" : "Selecciona el personal (solo operarios con asistencia activa)"}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onChange={async (value) => {
                    if (value) {
                      // Obtener la unidad asignada al personal desde la asistencia
                      try {
                        const asistenciaResponse = await api.get(`/asistencias?personal_id=${value}&unidad=1`);
                        console.log('Respuesta de asistencia:', asistenciaResponse.data);
                        
                        if (asistenciaResponse.data && asistenciaResponse.data.unidad_id) {
                          const unidadId = asistenciaResponse.data.unidad_id;
                          let kmActual = null;
                          
                          // Si la respuesta de asistencia ya tiene km_actual, usarlo directamente
                          if (asistenciaResponse.data.km_actual !== null && asistenciaResponse.data.km_actual !== undefined) {
                            kmActual = parseFloat(asistenciaResponse.data.km_actual);
                            console.log('Kilometraje obtenido desde asistencia:', kmActual);
                          } else {
                            // Si no, obtener el kilometraje actual directamente de la unidad
                            try {
                              const unidadResponse = await api.get(`/unidades/${unidadId}`);
                              console.log('Respuesta de unidad:', unidadResponse.data);
                              if (unidadResponse.data && unidadResponse.data.km_actual !== null && unidadResponse.data.km_actual !== undefined) {
                                kmActual = parseFloat(unidadResponse.data.km_actual);
                                console.log('Kilometraje obtenido desde unidad:', kmActual);
                              }
                            } catch (error) {
                              console.error('Error al obtener kilometraje de la unidad:', error);
                            }
                          }
                          
                          // Establecer los valores en el formulario
                          const valuesToSet = {
                            unidad_id: unidadId
                          };
                          
                          if (kmActual !== null && kmActual !== undefined) {
                            valuesToSet.km_inicio = kmActual;
                          }
                          
                          console.log('Valores a establecer en el formulario:', valuesToSet);
                          form.setFieldsValue(valuesToSet);
                          
                          // Forzar actualización del formulario
                          setTimeout(() => {
                            const currentKmInicio = form.getFieldValue('km_inicio');
                            console.log('Valor actual de km_inicio en el formulario:', currentKmInicio);
                            if (currentKmInicio !== kmActual && kmActual !== null) {
                              form.setFieldsValue({ km_inicio: kmActual });
                            }
                          }, 100);
                        }
                      } catch (error) {
                        console.error('Error al obtener unidad del personal:', error);
                      }
                    } else {
                      form.setFieldsValue({ km_inicio: null, unidad_id: null });
                    }
                  }}
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {(esExtemporaneo ? personal : personalDisponible).map((p) => (
                    <Select.Option key={p.id} value={p.id}>
                      {p.nombre_completo} - {p.cargo}
                    </Select.Option>
                  ))}
                </Select>
                {!esExtemporaneo && personalDisponible.length === 0 && (
                  <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}>
                    No hay operarios con asistencia activa. Deben marcar su ingreso primero.
                  </div>
                )}
                {esExtemporaneo && personal.length === 0 && (
                  <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}>
                    No hay personal activo disponible.
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="km_inicio" label="Km. Inicio del Servicio">
                <InputNumber disabled={modoSoloLectura} min={0} precision={2} style={{ width: '100%' }} placeholder="Se carga automáticamente al asignar personal" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="km_fin" label="Km. Fin del Servicio">
                <InputNumber disabled={modoSoloLectura} min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="observaciones" label="Observaciones">
            <Input.TextArea disabled={modoSoloLectura} rows={4} />
          </Form.Item>

          <Divider>Imágenes del Servicio</Divider>
          <Form.Item>
            <Upload
              disabled={modoSoloLectura}
              name="imagenes"
              multiple
              fileList={fileList}
              onChange={({ fileList: newFileList }) => {
                setFileList(newFileList);
              }}
              beforeUpload={() => false}
              accept="image/*"
              listType="picture-card"
              onPreview={(file) => {
                const imageUrl = getImageUrl(file.url || file.thumbUrl || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : ''));
                setPreviewImage(imageUrl);
                setPreviewVisible(true);
              }}
              onRemove={(file) => {
                const newFileList = fileList.filter((item) => item.uid !== file.uid);
                setFileList(newFileList);
                return true;
              }}
              customRequest={({ onSuccess }) => {
                setTimeout(() => {
                  onSuccess("ok");
                }, 0);
              }}
            >
              {fileList.length < 10 && (
                <div>
                  <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                  <div style={{ marginTop: 8 }}>Subir</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Divider>Plano Interactivo - Marcado de Fallas Mecánicas</Divider>
          <Form.Item name="fallas_mecanicas">
            <PlanoCarro disabled={modoSoloLectura} />
          </Form.Item>

          <Divider>Checklist de Elementos del Vehículo</Divider>
          <Form.Item shouldUpdate={(prevValues, currentValues) => {
            // Forzar actualización cuando cambien los valores del checklist
            const checklistKeys = checklistItems.map(item => item.key);
            return checklistKeys.some(key => prevValues[key] !== currentValues[key]);
          }}>
            {() => {
              // Agrupar elementos en pares para mostrar 2 por línea
              const groupedItems = [];
              for (let i = 0; i < checklistItems.length; i += 2) {
                groupedItems.push(checklistItems.slice(i, i + 2));
              }

              return (
                <div style={{ marginBottom: 16 }}>
                  {groupedItems.map((pair, pairIndex) => (
                    <Row gutter={[16, 8]} key={pairIndex} style={{ marginBottom: 8 }}>
                      {pair.map((item) => {
                        const currentValue = form.getFieldValue(item.key);
                        const isSI = currentValue === 'SI';
                        const isNO = currentValue === 'NO';

                        return (
                          <Col span={12} key={item.key}>
                            <Row gutter={[8, 0]}>
                              <Col span={12} style={{ display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: '13px' }}>
                                {item.label}
                              </Col>
                              <Col span={6}>
                                <Form.Item name={item.key} style={{ marginBottom: 0 }}>
                                  <div
                                    style={{
                                      padding: '8px 12px',
                                      border: '1px solid #d9d9d9',
                                      borderRadius: '2px',
                                      textAlign: 'center',
                                      cursor: modoSoloLectura ? 'not-allowed' : 'pointer',
                                      background: isSI ? '#e6f7ff' : '#fff',
                                      borderColor: isSI ? '#1890ff' : '#d9d9d9',
                                      transition: 'all 0.2s',
                                      userSelect: 'none',
                                      minHeight: '36px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '13px',
                                      opacity: modoSoloLectura ? 0.6 : 1,
                                    }}
                                    onClick={() => {
                                      if (modoSoloLectura) return;
                                      if (isSI) {
                                        form.setFieldsValue({ [item.key]: undefined });
                                      } else {
                                        form.setFieldsValue({ [item.key]: 'SI' });
                                      }
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSI) {
                                        e.currentTarget.style.background = '#f5f5f5';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSI) {
                                        e.currentTarget.style.background = '#fff';
                                      }
                                    }}
                                  >
                                    SI
                                  </div>
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Form.Item name={item.key} style={{ marginBottom: 0 }}>
                                  <div
                                    style={{
                                      padding: '8px 12px',
                                      border: '1px solid #d9d9d9',
                                      borderRadius: '2px',
                                      textAlign: 'center',
                                      cursor: modoSoloLectura ? 'not-allowed' : 'pointer',
                                      background: isNO ? '#fff1f0' : '#fff',
                                      borderColor: isNO ? '#ff4d4f' : '#d9d9d9',
                                      transition: 'all 0.2s',
                                      userSelect: 'none',
                                      minHeight: '36px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '13px',
                                      opacity: modoSoloLectura ? 0.6 : 1,
                                    }}
                                    onClick={() => {
                                      if (modoSoloLectura) return;
                                      if (isNO) {
                                        form.setFieldsValue({ [item.key]: undefined });
                                      } else {
                                        form.setFieldsValue({ [item.key]: 'NO' });
                                      }
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isNO) {
                                        e.currentTarget.style.background = '#f5f5f5';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isNO) {
                                        e.currentTarget.style.background = '#fff';
                                      }
                                    }}
                                  >
                                    NO
                                  </div>
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                        );
                      })}
                    </Row>
                  ))}
                </div>
              );
            }}
          </Form.Item>
          
          <Divider />
          
          <Form.Item name="estado" label="Estado" initialValue="pendiente">
            <Select disabled={modoSoloLectura}>
              <Select.Option value="pendiente">Pendiente</Select.Option>
              <Select.Option value="en_espera">En Espera</Select.Option>
              <Select.Option value="en_proceso">En Proceso</Select.Option>
              <Select.Option value="completado">Completado</Select.Option>
              <Select.Option value="cancelado">Cancelado</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item name="costo" label="Costo">
            <InputNumber disabled={modoSoloLectura} min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="monto_descuento" label="Monto de Descuento">
            <InputNumber disabled={modoSoloLectura} min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
          </Form.Item>

          <Divider>Adelanto</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="monto_adelanto" label="Monto de Adelanto">
                <InputNumber disabled={modoSoloLectura} min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="metodo_pago_adelanto_id" label="Método de Pago del Adelanto">
                <Select disabled={modoSoloLectura} placeholder="Selecciona el método de pago" allowClear>
                  {metodosPago.map((metodo) => (
                    <Select.Option key={metodo.id} value={metodo.id}>
                      {metodo.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item shouldUpdate={(prevValues, currentValues) => 
            prevValues.metodo_pago_adelanto_id !== currentValues.metodo_pago_adelanto_id
          }>
            {() => {
              const metodoPagoAdelantoId = form.getFieldValue('metodo_pago_adelanto_id');
              const metodoPagoAdelanto = metodosPago.find(m => m.id === metodoPagoAdelantoId);
              const esEfectivo = metodoPagoAdelanto?.nombre?.toLowerCase().includes('efectivo');
              
              if (metodoPagoAdelantoId && !esEfectivo) {
                return (
                  <Form.Item label="Comprobante de Adelanto">
                    <Upload
                      disabled={modoSoloLectura}
                      name="comprobante_adelanto"
                      listType="picture-card"
                      fileList={comprobanteAdelanto}
                      onChange={({ fileList: newFileList }) => {
                        setComprobanteAdelanto(newFileList);
                      }}
                      beforeUpload={() => false}
                      accept="image/*"
                      maxCount={1}
                      onPreview={(file) => {
                        window.open(file.url || file.thumbUrl, '_blank');
                      }}
                      customRequest={({ onSuccess }) => {
                        setTimeout(() => {
                          onSuccess("ok");
                        }, 0);
                      }}
                    >
                      {comprobanteAdelanto.length < 1 && (
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>Subir</div>
                        </div>
                      )}
                    </Upload>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          {!modoSoloLectura && (
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
                  {editingServicio ? 'Actualizar' : 'Crear'}
                </Button>
                <Button
                  onClick={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setEditingServicio(null);
                    setModoSoloLectura(false);
                  }}
                >
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          )}
          {modoSoloLectura && (
            <Form.Item>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingServicio(null);
                  setModoSoloLectura(false);
                }}
              >
                Cerrar
              </Button>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Modal para crear cliente */}
      <Modal
        title="Nuevo Cliente"
        open={clienteModalVisible}
        onCancel={() => {
          setClienteModalVisible(false);
          clienteForm.resetFields();
        }}
        footer={null}
      >
        <Form form={clienteForm} layout="vertical" onFinish={handleCrearCliente}>
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[{ required: true, message: 'Ingresa el nombre' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="apellido"
            label="Apellido"
            rules={[{ required: true, message: 'Ingresa el apellido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Email inválido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="telefono" label="Teléfono">
            <Input />
          </Form.Item>
          <Form.Item name="dni" label="DNI">
            <Input />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Crear
              </Button>
              <Button onClick={() => {
                setClienteModalVisible(false);
                clienteForm.resetFields();
              }}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal para crear vehículo */}
      <Modal
        title="Nuevo Vehículo"
        open={vehiculoModalVisible}
        onCancel={() => {
          setVehiculoModalVisible(false);
          vehiculoForm.resetFields();
        }}
        footer={null}
      >
        <Form form={vehiculoForm} layout="vertical" onFinish={handleCrearVehiculo}>
          <Form.Item
            name="placa"
            label="Placa"
            rules={[{ required: true, message: 'Ingresa la placa' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="marca"
            label="Marca"
            rules={[{ required: true, message: 'Ingresa la marca' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="modelo"
            label="Modelo"
            rules={[{ required: true, message: 'Ingresa el modelo' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="anio" label="Año">
            <InputNumber min={1900} max={new Date().getFullYear() + 1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="color" label="Color">
            <Input />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Crear
              </Button>
              <Button onClick={() => {
                setVehiculoModalVisible(false);
                vehiculoForm.resetFields();
              }}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
        </Modal>

        {/* Modal de preview de imágenes */}
        <Modal
          open={previewVisible}
          footer={null}
          onCancel={() => setPreviewVisible(false)}
          width="95%"
          style={{ maxWidth: 800 }}
          centered
        >
          <img alt="preview" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      </div>
    );
  };
  
  export default Servicios;
